import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useForm, useFormState } from 'react-hook-form';
import { Col, Form, FormLabel } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import * as yup from "yup";
import { yupResolver } from '@hookform/resolvers/yup';
import {
  ADMINISTRATION_LOGO_URL,
  ADMINISTRATION_NAME,
  SENDER_ID,
  SYSTEM_DEFAULT_COUNTRY,
  SYSTEM_UNION_CODE
} from '../../config';
import CustomFormTextArea from '../forms/CustomFormTextArea';
import CustomFormSwitch from '../forms/CustomFormSwitch';

// Define a validation schema for the form.
const schema = yup.object({
  contactInstructions: yup.string().required(),
}).required();

const SystemSettingsForm = ({ defaultValues, onSubmit, onError }) => {

  const unionModeEnabled = Boolean(SYSTEM_UNION_CODE);

  const { t } = useTranslation();

  const { register, formState: { errors, isDirty }, handleSubmit, reset, control } = useForm({
    mode: "onChange",
    defaultValues: defaultValues,
    resolver: yupResolver(schema)
  });

  const { isValid, dirtyFields, isSubmitting } = useFormState({
    control
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const displayCountryCode = defaultValues?.countryCode ?? SYSTEM_DEFAULT_COUNTRY ?? '';
  const displayCountryLabel = displayCountryCode
    ? t(`countries:${displayCountryCode}`, { defaultValue: displayCountryCode })
    : t('common:lblNotSpecified', { defaultValue: 'Not specified' });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      { !unionModeEnabled && (
      <>
        <div className="row">

          <div className="col-xs-12 col-md-6 mb-3">

            <FormLabel className="fw-bold" >{t('systemSettings:lblCountry')}</FormLabel>
            <input className="form-control-plaintext" readOnly value={displayCountryLabel} />
          </div>
          <div className="col-xs-12 col-md-6 mb-3">

            <FormLabel className="fw-bold" >{t('systemSettings:lblSenderId')}</FormLabel>
            <input className="form-control-plaintext" readOnly value={defaultValues?.senderId ?? SENDER_ID ?? ''} />
          </div>
        </div>
        <div className="row">

          <div className="col-xs-12 col-md-6 mb-3">

            <FormLabel className="fw-bold" htmlFor="administrationName">{t('systemSettings:lblAdministrationName')}</FormLabel>
            <input className="form-control-plaintext" readOnly value={defaultValues?.administrationName ?? ADMINISTRATION_NAME ?? ''} />
          </div>

          <div className="col-xs-12 col-md-6 mb-3">

            <FormLabel className="fw-bold" htmlFor="administrationLogo">{t('systemSettings:lblAdministrationLogo')}</FormLabel>

            {(defaultValues?.administrationLogoUrl ?? ADMINISTRATION_LOGO_URL) ? <>
              <img src={defaultValues?.administrationLogoUrl ?? ADMINISTRATION_LOGO_URL} className="mt-3 img img-responsive" alt="Logo"
                style={{ maxHeight: 100 }} />
              <p>{defaultValues?.administrationLogoUrl ?? ADMINISTRATION_LOGO_URL}</p>
            </> : <p>Not specified</p>}
          </div>
        </div>
        <hr />
      </>
      )}

      <div className="row">
        <div className="col-xs-12 mb-3">

          <FormLabel className="fw-bold" htmlFor="contactInstructions">{t('systemSettings:lblContactUsInstructions')}<span
            className="requiredIndicator">*</span> </FormLabel>
          <CustomFormTextArea fieldName='contactInstructions'
            params={{ required: true, rows:10 }}
            register={register}
            dirtyFields={dirtyFields}
            errorMessage={errors?.contactInstructions?.message} />
        </div>

      </div>

      <hr className="my-4" />

      <div className="row">
        <div className="col-12">
          <h5 className="fw-bold">{t('systemSettings:hdgDefaultUserPermissions')}</h5>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <h6 className="text-muted">{t('systemSettings:hdgCustomsUserDefaultPermissions')}</h6>
        </div>
      </div>

      <div className="row">
        <div className="col-xs-12 col-md-6 mb-3">
          <FormLabel className="fw-bold" htmlFor="defaultCanPerformOperations">
            {t('systemSettings:lblCanPerformOperations')}
          </FormLabel>
          <CustomFormSwitch fieldName='defaultCanPerformOperations'
            register={register}
            dirtyFields={dirtyFields}
            errorMessage={errors?.defaultCanPerformOperations?.message} />
        </div>
        <div className="col-xs-12 col-md-6 mb-3">
          <FormLabel className="fw-bold" htmlFor="defaultCanChangeOwnOffice">
            {t('systemSettings:lblCanChangeOwnOffice')}
          </FormLabel>
          <CustomFormSwitch fieldName='defaultCanChangeOwnOffice'
            register={register}
            dirtyFields={dirtyFields}
            errorMessage={errors?.defaultCanChangeOwnOffice?.message} />
        </div>
      </div>

      <div className="row mt-3">
        <div className="col-12">
          <h6 className="text-muted">{t('systemSettings:hdgAdminUserDefaultPermissions')}</h6>
        </div>
      </div>

      <div className="row">
        <div className="col-xs-12 col-md-6 mb-3">
          <FormLabel className="fw-bold" htmlFor="defaultAdminCanManageSysOptionsAndUsers">
            {t('systemSettings:lblCanManageSysOptionsAndUsers')}
          </FormLabel>
          <CustomFormSwitch fieldName='defaultAdminCanManageSysOptionsAndUsers'
            register={register}
            dirtyFields={dirtyFields}
            errorMessage={errors?.defaultAdminCanManageSysOptionsAndUsers?.message} />
        </div>
        <div className="col-xs-12 col-md-6 mb-3">
          <FormLabel className="fw-bold" htmlFor="defaultAdminCanManageSettings">
            {t('systemSettings:lblCanManageSettings')}
          </FormLabel>
          <CustomFormSwitch fieldName='defaultAdminCanManageSettings'
            register={register}
            dirtyFields={dirtyFields}
            errorMessage={errors?.defaultAdminCanManageSettings?.message} />
        </div>
      </div>

      <Form.Group className="row">
        <Col>
          <button type="submit" color="primary" className="btn btn-success  mt-3 me-2"
            disabled={!isValid || isSubmitting || !isDirty}>
            {t('common:btnSaveChanges')}
          </button>
        </Col>
      </Form.Group>
    </form>
  );
};

SystemSettingsForm.propTypes = {
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

export default SystemSettingsForm;
